import api from './api';
import { KudosClassification, SpringPageResponse } from '../types';

export interface Kudos {
  id: string;
  fromUserId: string;
  fromUserEmail: string;
  fromUserName: string;
  toUserId: string;
  toUserEmail: string;
  toUserName: string;
  message: string;
  slackChannel?: string;
  slackMessageTs?: string;
  createdAt: string;
  classification?: KudosClassification;
  classificationConfidence?: number;
  classificationReasoning?: string;
  isReviewed?: boolean;
  isHidden?: boolean;
}

interface RawKudosResponse {
  id: string;
  fromUserId: string;
  fromUserEmail: string;
  fromUserName: string;
  toUserId: string;
  toUserEmail: string;
  toUserName: string;
  message: string;
  slackChannel?: string;
  slackMessageTs?: string;
  created?: string;
  createdAt?: string;
  classification?: KudosClassification;
  classificationConfidence?: number;
  classificationReasoning?: string;
  reviewed?: boolean;
  isReviewed?: boolean;
  hidden?: boolean;
  isHidden?: boolean;
}

export interface CreateKudosDto {
  fromUserId: string;
  fromUserEmail: string;
  fromUserName: string;
  toUserId: string;
  toUserEmail: string;
  toUserName: string;
  message: string;
  slackChannel?: string;
  slackMessageTs?: string;
}

export interface ReviewKudosDto {
  approved: boolean;
  classification?: KudosClassification;
}

export interface PaginatedKudosResponse {
  kudos: Kudos[];
  total: number;
  page: number;
  totalPages: number;
}

export enum NotificationTemplateKey {
  BIRTHDAY = 'BIRTHDAY',
  WORK_ANNIVERSARY = 'WORK_ANNIVERSARY',
  INVOICE_SUBMITTED = 'INVOICE_SUBMITTED',
  INVOICE_AWAITING_APPROVAL = 'INVOICE_AWAITING_APPROVAL',
  ANNIVERSARY_REVIEW_REMINDER = 'ANNIVERSARY_REVIEW_REMINDER',
  START_DATE_MILESTONE = 'START_DATE_MILESTONE',
  QUARTERLY_EVAL_REMINDER = 'QUARTERLY_EVAL_REMINDER',
}

export interface AnniversaryMilestone {
  year: number;
  message: string | null;
  enabled: boolean;
}

/**
 * Behavioral configuration carried on the WORK_ANNIVERSARY notification
 * template (jsonb `config` column). Null for all other template keys.
 */
export interface AnniversaryNotificationConfig {
  channel: string | null;
  notificationHour: number;
  sendDmToEmployee: boolean;
  tagEmployee: boolean;
  tagHere: boolean;
  milestones: AnniversaryMilestone[];
}

/**
 * Organization roles that may be selected as review reminder recipients.
 * MEMBER stands for a "Leader": a member with people reporting to them.
 */
export enum ReviewReminderNotifyRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MEMBER = 'MEMBER',
}

/** Whether every matching recipient is notified, or only the closest one. */
export enum ReviewReminderSendMode {
  NOTIFY_ALL = 'NOTIFY_ALL',
  NOTIFY_FIRST = 'NOTIFY_FIRST',
}

/**
 * Behavioral configuration carried on the ANNIVERSARY_REVIEW_REMINDER
 * notification template (jsonb `config` column). Presented in the UI as
 * "Performance Review Reminder". Null for all other template keys.
 */
export interface ReviewReminderNotificationConfig {
  channel: string | null;
  sendToChannel: boolean;
  notificationHour: number;
  daysBefore: number[];
  notifyRoles: ReviewReminderNotifyRole[];
  sendMode: ReviewReminderSendMode;
}

/**
 * Organization roles that may be selected as start-date milestone check-in
 * recipients. MEMBER stands for a "Leader": a member with people reporting to
 * them.
 */
export enum MilestoneCheckInNotifyRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MEMBER = 'MEMBER',
}

/** Whether every matching recipient is notified, or only the closest one. */
export enum MilestoneCheckInSendMode {
  NOTIFY_ALL = 'NOTIFY_ALL',
  NOTIFY_FIRST = 'NOTIFY_FIRST',
}

/**
 * Behavioral configuration carried on the START_DATE_MILESTONE notification
 * template (jsonb `config` column). Presented in the UI as "Start Date
 * Milestone Check-in". DM-only, so it carries no channel settings. Null for all
 * other template keys.
 */
export interface MilestoneCheckInNotificationConfig {
  notificationHour: number;
  milestoneDays: number[];
  notifyRoles: MilestoneCheckInNotifyRole[];
  sendMode: MilestoneCheckInSendMode;
}

/**
 * Organization roles that may be selected as quarterly eval reminder
 * recipients. MEMBER stands for a "Leader": a member with people reporting to
 * them.
 */
export enum QuarterlyEvalReminderNotifyRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MEMBER = 'MEMBER',
}

/**
 * Behavioral configuration carried on the QUARTERLY_EVAL_REMINDER notification
 * template (jsonb `config` column). Presented in the UI as "Quarterly Eval
 * Reminder". DM-only, so it carries no channel settings; delivery is fixed to
 * notifying every matching recipient. Null for all other template keys.
 */
export interface QuarterlyEvalReminderNotificationConfig {
  notificationHour: number;
  notifyRoles: QuarterlyEvalReminderNotifyRole[];
}

export type NotificationTemplateConfig =
  | AnniversaryNotificationConfig
  | ReviewReminderNotificationConfig
  | MilestoneCheckInNotificationConfig
  | QuarterlyEvalReminderNotificationConfig;

export interface NotificationTemplate {
  id: string;
  organizationId: string;
  templateKey: NotificationTemplateKey;
  body: string;
  enabled: boolean;
  allowedParams: string[];
  config: NotificationTemplateConfig | null;
  created: string;
  updated: string;
}

export interface UpdateNotificationTemplateDto {
  templateKey: NotificationTemplateKey;
  body?: string;
  enabled?: boolean;
  restoreToDefault?: boolean;
  config?: NotificationTemplateConfig;
}

/**
 * The organization's Slack connection, including the channels used for each
 * kind of notification.
 */
export interface SlackConnectionResponse {
  id: string;
  organizationId: string;
  connected: boolean;
  workspaceName: string | null;
  teamId: string | null;
  kudosChannel: string | null;
  birthdayChannel: string | null;
  anniversaryChannel: string | null;
  invoiceChannel: string | null;
  connectedAt: string | null;
  created: string;
  updated: string;
}

/** Slack connection update payload. Omitted fields are left unchanged. */
export interface UpdateSlackConnectionDto {
  botToken?: string;
  signingSecret?: string;
  kudosChannel?: string;
  birthdayChannel?: string;
  anniversaryChannel?: string;
  invoiceChannel?: string;
}

export interface SyncUsersResponse {
  synced: number;
  updated: number;
  notFound: number;
  details: Array<{
    email: string;
    slackUserId: string;
    displayName: string;
    status: 'updated' | 'not_found' | 'no_change';
  }>;
}

/**
 * Slack Service
 * Centralizes all Slack-related API calls
 */
class SlackService {
  private baseUrl = '/slack';

  private mapKudos(raw: RawKudosResponse): Kudos {
    return {
      id: raw.id,
      fromUserId: raw.fromUserId,
      fromUserEmail: raw.fromUserEmail,
      fromUserName: raw.fromUserName,
      toUserId: raw.toUserId,
      toUserEmail: raw.toUserEmail,
      toUserName: raw.toUserName,
      message: raw.message,
      slackChannel: raw.slackChannel,
      slackMessageTs: raw.slackMessageTs,
      createdAt: raw.created ?? raw.createdAt ?? new Date().toISOString(),
      classification: raw.classification,
      classificationConfidence: raw.classificationConfidence,
      classificationReasoning: raw.classificationReasoning,
      isReviewed: raw.reviewed ?? raw.isReviewed,
      isHidden: raw.hidden ?? raw.isHidden,
    };
  }

  private mapKudosList(rawList: RawKudosResponse[]): Kudos[] {
    return rawList.map((raw) => this.mapKudos(raw));
  }

  /**
   * Create a kudos record
   */
  async createKudos(data: CreateKudosDto): Promise<Kudos> {
    const response = await api.post<RawKudosResponse>(`${this.baseUrl}/kudos`, data);
    return this.mapKudos(response.data);
  }

  /**
   * Get kudos received by a user
   */
  async getKudosForUser(userId: string, page: number = 1, size: number = 20): Promise<PaginatedKudosResponse> {
    // Backend uses 0-indexed pages
    const response = await api.get<SpringPageResponse<RawKudosResponse>>(`${this.baseUrl}/kudos/user/${userId}?page=${page - 1}&size=${size}&sort=created,desc`);
    const data = response.data;
    if (data && 'content' in data) {
      return {
        kudos: this.mapKudosList(data.content),
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    return { kudos: [], total: 0, page: 1, totalPages: 0 };
  }

  /**
   * Get kudos sent by a user
   */
  async getKudosFromUser(userId: string, page: number = 1, size: number = 20): Promise<PaginatedKudosResponse> {
    // Backend uses 0-indexed pages
    const response = await api.get<SpringPageResponse<RawKudosResponse>>(`${this.baseUrl}/kudos/from/${userId}?page=${page - 1}&size=${size}&sort=created,desc`);
    const data = response.data;
    if (data && 'content' in data) {
      return {
        kudos: this.mapKudosList(data.content),
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    return { kudos: [], total: 0, page: 1, totalPages: 0 };
  }

  /**
   * Get all kudos (admin only)
   */
  async getAllKudos(page: number = 1, size: number = 20): Promise<PaginatedKudosResponse> {
    // Backend uses 0-indexed pages
    const response = await api.get<SpringPageResponse<RawKudosResponse>>(`${this.baseUrl}/kudos?page=${page - 1}&size=${size}`);
    const data = response.data;
    if (data && 'content' in data) {
      return {
        kudos: this.mapKudosList(data.content),
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    return { kudos: [], total: 0, page: 1, totalPages: 0 };
  }

  /**
   * Get unreviewed kudos messages (admin only)
   */
  async getUnreviewedKudos(page: number = 1, size: number = 20): Promise<PaginatedKudosResponse> {
    // Backend uses 0-indexed pages
    const response = await api.get<SpringPageResponse<RawKudosResponse>>(`${this.baseUrl}/kudos/unreviewed?page=${page - 1}&size=${size}`);
    const data = response.data;
    if (data && 'content' in data) {
      return {
        kudos: this.mapKudosList(data.content),
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    return { kudos: [], total: 0, page: 1, totalPages: 0 };
  }

  /**
   * Get non-kudos messages (admin only)
   */
  async getNonKudosMessages(page: number = 1, size: number = 20): Promise<PaginatedKudosResponse> {
    // Backend uses 0-indexed pages
    const response = await api.get<SpringPageResponse<RawKudosResponse>>(`${this.baseUrl}/kudos/non-kudos?page=${page - 1}&size=${size}`);
    const data = response.data;
    if (data && 'content' in data) {
      return {
        kudos: this.mapKudosList(data.content),
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    return { kudos: [], total: 0, page: 1, totalPages: 0 };
  }

  /**
   * Get a single kudos by ID
   */
  async getKudosById(id: string): Promise<Kudos> {
    const response = await api.get<RawKudosResponse>(`${this.baseUrl}/kudos/${id}`);
    return this.mapKudos(response.data);
  }

  /**
   * Review a kudos (approve/reject with optional classification update)
   */
  async reviewKudos(id: string, data: ReviewKudosDto): Promise<Kudos> {
    const response = await api.patch<RawKudosResponse>(`${this.baseUrl}/kudos/${id}/review`, data);
    return this.mapKudos(response.data);
  }

  /**
   * Hide a kudos from display
   */
  async hideKudos(id: string): Promise<Kudos> {
    const response = await api.patch<RawKudosResponse>(`${this.baseUrl}/kudos/${id}/hide`);
    return this.mapKudos(response.data);
  }

  /**
   * Delete a kudos (admin only)
   */
  async deleteKudos(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/kudos/${id}`);
  }

  /**
   * Get paginated kudos with filters
   */
  async getPaginatedKudos(
    toUserId?: string,
    fromUserId?: string,
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedKudosResponse> {
    const params = new URLSearchParams();
    if (toUserId) params.append('toUserId', toUserId);
    if (fromUserId) params.append('fromUserId', fromUserId);
    // Backend uses 0-indexed pages
    params.append('page', (page - 1).toString());
    params.append('size', limit.toString());

    const response = await api.get<SpringPageResponse<RawKudosResponse> | PaginatedKudosResponse>(
      `${this.baseUrl}/kudos?${params.toString()}`
    );
    const data = response.data;
    if (data && 'content' in data) {
      return {
        kudos: this.mapKudosList(data.content),
        total: data.totalElements,
        page: data.number + 1, // Convert 0-indexed to 1-indexed
        totalPages: data.totalPages,
      };
    }
    const paginatedData = data as PaginatedKudosResponse;
    return {
      ...paginatedData,
      kudos: this.mapKudosList(paginatedData.kudos as unknown as RawKudosResponse[]),
    };
  }

  /**
   * Sync Slack user IDs with system users
   */
  async syncSlackUsers(): Promise<SyncUsersResponse> {
    const response = await api.post<SyncUsersResponse>(`${this.baseUrl}/sync-users`);
    return response.data;
  }

  /**
   * Test birthday notification
   */
  async testBirthdayNotification(
    userName: string,
    userId?: string,
    userPhotoUrl?: string
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      `${this.baseUrl}/test/birthday`,
      { userName, userId, userPhotoUrl }
    );
    return response.data;
  }

  /**
   * Test anniversary notification
   */
  async testAnniversaryNotification(
    userName: string,
    years: number,
    userId?: string,
    userPhotoUrl?: string
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      `${this.baseUrl}/test/anniversary`,
      { userName, years, userId, userPhotoUrl }
    );
    return response.data;
  }

  /**
   * Sync emojis from Slack workspace (admin only)
   */
  async syncEmojis(): Promise<{ synced: number; aliases: number }> {
    const response = await api.post<{ synced: number; aliases: number }>(
      `${this.baseUrl}/emojis/sync`
    );
    return response.data;
  }

  /**
   * Get all synced Slack emojis as a map of shortcode -> imageUrl
   */
  async getEmojis(): Promise<Record<string, string>> {
    const response = await api.get<Record<string, string>>(
      `${this.baseUrl}/emojis`
    );
    return response.data;
  }

  /**
   * Get the organization's editable notification message templates.
   * Templates are seeded from defaults on first access (admin only).
   */
  async getTemplates(): Promise<NotificationTemplate[]> {
    const response = await api.get<NotificationTemplate[]>(`${this.baseUrl}/templates`);
    return response.data;
  }

  /**
   * Update one or more notification templates. Templates cannot be
   * created or deleted, only edited (admin only).
   */
  async updateTemplates(templates: UpdateNotificationTemplateDto[]): Promise<NotificationTemplate[]> {
    const response = await api.put<NotificationTemplate[]>(`${this.baseUrl}/templates`, { templates });
    return response.data;
  }

  /**
   * Get the organization's Slack connection and notification channels.
   */
  async getConnection(): Promise<SlackConnectionResponse> {
    const response = await api.get<SlackConnectionResponse>(`${this.baseUrl}/connection`);
    return response.data;
  }

  /**
   * Update the organization's Slack connection (admin/manager only).
   */
  async updateConnection(data: UpdateSlackConnectionDto): Promise<SlackConnectionResponse> {
    const response = await api.put<SlackConnectionResponse>(`${this.baseUrl}/connection`, data);
    return response.data;
  }

}

// Export singleton instance
export const slackService = new SlackService();
